import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

/* ===== Price Alerts — candle-close level alerts for manual trading =====
   "Tell me when the 15m candle CLOSES at/above X (or at/below Y) on XAUUSDT."
   Close-based (wicks ignored), one-shot (re-arm after firing), any Shark
   contract, multiple alerts. Fires: screen banner + siren (global, any tab),
   Telegram, and the log below. */

const when = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) : "—";

export default function PriceAlerts() {
  const [st, setSt] = useState(null);
  const [form, setForm] = useState({ symbol: "XAUUSDT", tf: "15m", condition: "close_above", level: "", note: "" });
  const [msg, setMsg] = useState("");
  const load = useCallback(async () => {
    try { const r = await axios.get("/api/price-alerts", { timeout: 15000 }); setSt(r.data); } catch {}
  }, []);
  useEffect(() => { load(); const h = setInterval(load, 15000); return () => clearInterval(h); }, [load]);

  const submit = async () => {
    setMsg("");
    try {
      const r = await axios.post("/api/price-alerts", { ...form, level: +form.level });
      if (r.data?.error) setMsg(r.data.error);
      else { setMsg("✓ armed"); setForm(f => ({ ...f, level: "", note: "" })); }
    } catch (e) { setMsg(String(e.message || e)); }
    load();
  };
  const act = async (p, id) => { try { await axios.post(`/api/price-alerts/${p}`, { id }); } catch {} load(); };

  const inp = { background: "var(--panel3)", color: "var(--text2)", border: "1px solid var(--border2)", borderRadius: 6, padding: "4px 8px", fontSize: 12 };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h2>Price Alerts <span style={{ fontSize: 12, color: "var(--muted2)", fontWeight: 400 }}>candle-CLOSE level alerts · screen banner + siren + Telegram · for manual trading while you work</span></h2>
          <p className="dashboard-subtitle">
            Close-based on purpose: a wick through your level is noise — a candle <b>closing</b> through it is information.
            One-shot: an alert disarms when it fires; re-arm it from the list. The banner and siren fire on whichever tab of the app is open.
          </p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
        <input style={{ ...inp, width: 110 }} placeholder="SYMBOL" value={form.symbol}
          onChange={e => setForm(f => ({ ...f, symbol: e.target.value.toUpperCase() }))} />
        <select style={inp} value={form.tf} onChange={e => setForm(f => ({ ...f, tf: e.target.value }))}>
          {["1m", "5m", "15m", "30m", "1h", "4h", "1d"].map(x => <option key={x}>{x}</option>)}
        </select>
        <select style={inp} value={form.condition} onChange={e => setForm(f => ({ ...f, condition: e.target.value }))}>
          <option value="close_above">candle closes AT/ABOVE</option>
          <option value="close_below">candle closes AT/BELOW</option>
        </select>
        <input style={{ ...inp, width: 110 }} type="number" step="any" placeholder="level" value={form.level}
          onChange={e => setForm(f => ({ ...f, level: e.target.value }))} />
        <input style={{ ...inp, width: 200 }} placeholder="note (optional, goes in the alert)" value={form.note}
          onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
        <button className="btn btn-sm btn-primary" disabled={!form.symbol || !form.level} onClick={submit}>🔔 Arm alert</button>
        {msg && <span style={{ fontSize: 11, color: msg.startsWith("✓") ? "#22c55e" : "#ef4444" }}>{msg}</span>}
      </div>

      <div className="tl-table-wrap">
        <table className="stocks-table" style={{ width: "100%" }}>
          <thead><tr><th>Status</th><th>Symbol</th><th>TF</th><th>Condition</th><th>Level</th><th>Note</th><th>Set (IST)</th><th>Fired (IST)</th><th>Close</th><th></th></tr></thead>
          <tbody>
            {(st?.alerts || []).map(a => (
              <tr key={a.id}>
                <td>{a.active ? <span className="positive">● armed</span> : <span style={{ color: "#f59e0b" }}>✓ fired</span>}</td>
                <td style={{ fontWeight: 600 }}>{a.symbol}</td>
                <td>{a.tf}</td>
                <td style={{ fontSize: 11 }}>{a.condition === "close_above" ? "close ≥" : "close ≤"}</td>
                <td><b>{a.level}</b></td>
                <td style={{ fontSize: 11, color: "var(--muted2)" }}>{a.note}</td>
                <td style={{ whiteSpace: "nowrap", fontSize: 11 }}>{when(a.createdAt)}</td>
                <td style={{ whiteSpace: "nowrap", fontSize: 11 }}>{when(a.triggeredAt)}</td>
                <td>{a.triggeredClose ?? "—"}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {!a.active && <button className="btn btn-sm btn-secondary" onClick={() => act("rearm", a.id)} title="Arm again from now">↻ re-arm</button>}{" "}
                  <button className="btn btn-sm btn-danger" onClick={() => act("delete", a.id)}>✕</button>
                </td>
              </tr>
            ))}
            {!(st?.alerts || []).length && <tr><td colSpan={10} style={{ color: "var(--muted2)" }}>no alerts yet — arm one above (e.g., XAUUSDT 15m closes at/below 4080)</td></tr>}
          </tbody>
        </table>
      </div>

      {(st?.fires || []).length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", fontWeight: 700, marginBottom: 6 }}>Recent fires</div>
          {(st.fires || []).map((f, i) => (
            <div key={i} style={{ fontSize: 12, padding: "4px 0", borderBottom: "1px solid var(--panel3)" }}>
              <span style={{ color: "var(--muted2)" }}>{when(f.time)}</span> · 🔔 {f.msg}{f.note ? <span style={{ color: "var(--muted2)" }}> — {f.note}</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
